import ApiService from './api.service';
import type { DataWhitelist } from '../components/UpstreamFilterTable';

export type ListRecordsResponse<T> = {
  records: T[];
  total_pages?: number;
  total_records?: number;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export interface PipelineNodeShape {
  total_records?: number;
  columns?: any[];
  updated_at?: string;
}

export interface PipelineNodeMapOption {
  id?: string;
  source_node_id?: string;
  attribute_key?: string;
  attribute_id?: string;
  sub_options?: PipelineNodeMapOption[];
  managed_by_template_id?: string;
  managed_by_template?: boolean;
}

export interface PipelineNodeFieldTranslation {
  source: string;
  translation: string;
}

export interface PipelineNodeField {
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  description?: string;
  custom_sql?: string;
  transformer?: string;
  custom_transform_sql?: string;
  map_options?: PipelineNodeMapOption[];
  relationship_id?: string;
  translations?: PipelineNodeFieldTranslation[];
  part_of_composite_key?: boolean;
  taxonomic_id?: string;
  managed_by_template_id?: string;
  unlocked?: boolean;
  merge_behavior?: string;
}

export interface PipelineNodeSpecialNodeArgs {
  start_date?: string;
  end_date?: string;
  week_start_day_index?: number;
}

export interface DedupeRule {
  field_id: string;
  desc?: boolean;
  comparator?: string;
  value?: string;
}

export interface PipelineNode {
  id: string;
  label?: string;
  name?: string;
  table_name?: string;
  node_type: string;
  total_records?: number;
  taxonomic_id?: string;
  description?: string;
  marked_deleted?: boolean;
  shape?: PipelineNodeShape;
  build_error?: string;
  marked_stale_at?: string;
  last_build_completed?: string;
  custom_sql?: string;
  cached_generated_sql?: string;
  fields?: PipelineNodeField[];
  composite_key?: string[];
  last_modified_column?: string;
  flat_file?: boolean;
  source_id?: string;
  source_type?: string;
  upstream_node_ids?: string[];
  upstream_pre_filters?: Record<string, DataWhitelist>;
  data_whitelist?: DataWhitelist;
  grouping_behavior?: 'STACK' | 'MERGE' | 'DEDUPLICATE' | 'SUMMARIZE' | 'IDENTIFY';
  special_node_args?: PipelineNodeSpecialNodeArgs;
  incremental_build?: boolean;
  incremental_timestamp_field_id?: string;
  dedupe_rules?: DedupeRule[];
  include_in_snapshots?: boolean;
  simple_relationships?: boolean;
  source_record_type_id?: string;
  managed_by_template_id?: string;
  unlocked?: boolean;
  export_config?: {
    enabled?: boolean;
    replication_key_field_id?: string;
    cursor?: string;
  };
}

export class PipelineNodeService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
    search?: string;
  }): Promise<ListRecordsResponse<PipelineNode>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
      search = '',
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
    };

    // Backend expects filters as a JSON-encoded 'filter' query parameter
    if (Object.keys(filters).length > 0) {
      queryParams.filter = JSON.stringify(filters);
    }

    if (search) {
      queryParams.search = search;
    }

    const result = await ApiService.getInstance().request(
      'GET',
      '/pipelinenodes',
      queryParams
    ) as ListRecordsResponse<PipelineNode>;

    return result;
  }

  async findById(id: string): Promise<PipelineNode> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/pipelinenodes/${id}`
    ) as SingleRecordResponse<PipelineNode>;
    return result.record;
  }

  async save(node: Partial<PipelineNode>): Promise<PipelineNode> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/pipelinenodes/${node.id}`,
      node
    ) as SingleRecordResponse<PipelineNode>;
    return result.record;
  }

  async create(node: Partial<PipelineNode>): Promise<PipelineNode> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/pipelinenodes',
      node
    ) as SingleRecordResponse<PipelineNode>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/pipelinenodes/${id}`
    );
  }

  async copy(id: string, label: string): Promise<PipelineNode> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/pipelinenodes/${id}/copy`,
      { label }
    ) as SingleRecordResponse<PipelineNode>;
    return result.record;
  }
}

export interface BuildTreeNode {
  id: string;
  title: string;
  type: string;
  object_type: 'PipelineNode' | 'Measure' | 'Analysis' | 'Dashboard' | 'Dimension';
  object_id: string;
  pipeline_node_id?: string | null;  // Kept for backwards compatibility
}

export interface BuildTreeEdge {
  id: string;
  from_id: string;
  to_id: string;
  dependency_type: string;
}

export interface BuildTreeResponse {
  nodes: BuildTreeNode[];
  edges: BuildTreeEdge[];
}

export interface BuildTreeOptions {
  includeDimensions?: boolean;
  includeMeasures?: boolean;
  includeAnalyses?: boolean;
  includeDashboards?: boolean;
}

export async function getBuildTree(options?: BuildTreeOptions): Promise<BuildTreeResponse> {
  const params = new URLSearchParams();
  if (options?.includeDimensions) {
    params.append('include_dimensions', 'true');
  }
  if (options?.includeMeasures) {
    params.append('include_measures', 'true');
  }
  if (options?.includeAnalyses) {
    params.append('include_analyses', 'true');
  }
  if (options?.includeDashboards) {
    params.append('include_dashboards', 'true');
  }

  const queryString = params.toString();
  const url = queryString ? `/pipelinenodes/build-tree?${queryString}` : '/pipelinenodes/build-tree';

  const result = await ApiService.getInstance().request(
    'GET',
    url
  ) as BuildTreeResponse;
  // Ensure we never return undefined - React Query requires a defined value
  return result ?? { nodes: [], edges: [] };
}

export async function fetchFieldValues(compositeColumnId: string, query?: string): Promise<string[]> {
  if (!compositeColumnId) {
    return [];
  }
  const [pipelineNodeId, fieldId] = compositeColumnId.split(':');
  if (!pipelineNodeId || !fieldId) {
    return [];
  }
  const params: Record<string, string> = {};
  if (query) {
    params.q = query;
  }
  const result = await ApiService.getInstance().request(
    'GET',
    `/pipelinenodes/${pipelineNodeId}/column/${fieldId}/values`,
    Object.keys(params).length > 0 ? params : undefined
  ) as ListRecordsResponse<string>;
  return result.records;
}

export const pipelineNodeService = new PipelineNodeService();

